import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { HeartPulse } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalAssistantLogin = () => {
  const navigate = useNavigate();
  const [hospitalCode, setHospitalCode] = useState('');
  const [staffId, setStaffId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!hospitalCode.trim()) {
      toast.error('Please enter hospital ID');
      return;
    }

    // Validate hospital ID format (8 characters: STATE(2) + CITY(3) + RANDOM(3))
    if (hospitalCode.trim().length !== 8) {
      toast.error('Hospital ID must be exactly 8 characters (e.g., TSHYD446)');
      return;
    }
    
    if (!staffId.trim()) {
      toast.error('Please enter your staff ID');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    
    try {
      // First, verify hospital ID exists
      let hospital = null;
      try {
        const hospitalResponse = await axios.get(`${API}/hospitals/by-code/${hospitalCode.trim().toUpperCase()}`);
        hospital = hospitalResponse.data;
      } catch (error) {
        toast.error('Invalid hospital ID');
        setLoading(false);
        return;
      }

      // Auto-prepend "MA" prefix if not present
      let fullStaffId = staffId.trim().toUpperCase();
      if (!fullStaffId.startsWith('MA')) {
        fullStaffId = 'MA' + fullStaffId;
      }
      
      // Try to find medical assistant by ID and hospital
      const response = await axios.get(`${API}/medical-assistants?hospital_id=${hospital.id}`);
      const staff = response.data.find(s => s.id === fullStaffId);
      
      if (staff) {
        // Verify password
        if (staff.password !== password) {
          // Log failed login attempt
          try {
            await axios.post(`${API}/security/login-event`, {
              user_id: fullStaffId,
              user_name: staff.name || fullStaffId,
              role: 'medical_assistant',
              hospital_id: hospital.id,
              status: 'failed',
              failure_reason: 'Invalid password'
            });
          } catch (logError) {
            console.error('Failed to log login event:', logError);
          }
          toast.error('Invalid password');
          setLoading(false);
          return;
        }
        
        // Log successful login
        let loginId = null;
        try {
          const loginResponse = await axios.post(`${API}/security/login-event`, {
            user_id: fullStaffId,
            user_name: staff.name,
            role: 'medical_assistant',
            hospital_id: hospital.id,
            status: 'success'
          });
          loginId = loginResponse.data.login_id;
        } catch (logError) {
          console.error('Failed to log login event:', logError);
        }
        
        const userData = { ...staff, role: 'medical_assistant', loginId };
        
        // Store user data and hospital info in localStorage
        localStorage.setItem('healthcareUser', JSON.stringify(userData));
        localStorage.setItem('currentHospital', JSON.stringify(hospital));
        
        toast.success(`Welcome to ${hospital.name}, ${staff.name}!`);
        navigate('/medical-assistant-dashboard');
      } else {
        // Log failed login - user not found
        try {
          await axios.post(`${API}/security/login-event`, {
            user_id: fullStaffId,
            user_name: fullStaffId,
            role: 'medical_assistant',
            hospital_id: hospital.id,
            status: 'failed',
            failure_reason: 'Staff not found in hospital'
          });
        } catch (logError) {
          console.error('Failed to log login event:', logError);
        }
        toast.error('Medical Assistant not found in this hospital. Please check your Staff ID');
        setLoading(false);
        return;
      }
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <HeartPulse className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>
            Medical Assistant Portal
          </h1>
          <p className="text-base text-gray-600">Sign in to record patient vitals</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="hospitalCode" className="text-base">
                Hospital ID
              </Label>
              <Input
                id="hospitalCode"
                type="text"
                placeholder="Enter hospital ID (e.g., TSHYD304)"
                value={hospitalCode}
                onChange={(e) => setHospitalCode(e.target.value.toUpperCase().slice(0, 8))}
                maxLength={8}
                required
                className="h-12 text-base"
                data-testid="hospital-code-input"
              />
              <p className="text-xs text-gray-500">8 character code from your hospital admin</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="staffId" className="text-base">
                Staff ID
              </Label>
              <Input
                id="staffId"
                type="text"
                placeholder="Enter your staff ID (e.g., MA304123)"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value.toUpperCase())}
                required
                className="h-12 text-base"
                data-testid="staff-id-input"
              />
              <p className="text-xs text-gray-500">MA prefix will be added automatically</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-base">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 text-base"
                data-testid="password-input"
              />
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full h-12 text-base bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t text-center space-y-2">
            <p className="text-sm text-gray-600">
              Need help?{' '}
              <button
                onClick={() => navigate('/')}
                className="text-pink-600 hover:text-pink-700 font-medium"
              >
                Go to Home
              </button>
            </p>
            <p className="text-sm text-gray-600">
              Other Healthcare Staff?{' '}
              <button
                onClick={() => navigate('/healthcare-login')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Staff Portal
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalAssistantLogin;
